import React from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Typography,
  Box,
  Divider,
} from "@mui/material";

const DemographicsTab = ({ data, onChange }) => {
  const handleChange = (field, value) => {
    onChange({
      ...data,
      [field]: value,
    });
  };

  const genderOptions = ["Male", "Female", "Other", "Prefer not to say"];

  const ckdStages = [
    "1",
    "2",
    "3a",
    "3b",
    "4",
    "5",
    "5D",
  ];

  const stateOptions = [
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
  ];

  return (
    <Box>
      {/* Personal Information */}
      <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
        Personal Information
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="First Name"
            required
            value={data.firstName || ""}
            onChange={(e) => handleChange("firstName", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Middle Name"
            value={data.middleName || ""}
            onChange={(e) => handleChange("middleName", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Last Name"
            required
            value={data.lastName || ""}
            onChange={(e) => handleChange("lastName", e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Date of Birth"
            type="date"
            required
            InputLabelProps={{ shrink: true }}
            value={data.dateOfBirth || ""}
            onChange={(e) => handleChange("dateOfBirth", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            select
            label="Gender"
            required
            value={data.gender || ""}
            onChange={(e) => handleChange("gender", e.target.value)}
          >
            {genderOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Contact Information */}
      <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
        Contact Information
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            required
            value={data.email || ""}
            onChange={(e) => handleChange("email", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Phone"
            type="tel"
            required
            value={data.phone || ""}
            onChange={(e) => handleChange("phone", e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Street Address"
            required
            value={data.address || ""}
            onChange={(e) => handleChange("address", e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            label="City"
            required
            value={data.city || ""}
            onChange={(e) => handleChange("city", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            select
            label="State"
            required
            value={data.state || ""}
            onChange={(e) => handleChange("state", e.target.value)}
          >
            {stateOptions.map((state) => (
              <MenuItem key={state} value={state}>
                {state}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="ZIP Code"
            required
            value={data.zip || ""}
            onChange={(e) => handleChange("zip", e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Country"
            value={data.country || "United States"}
            onChange={(e) => handleChange("country", e.target.value)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Medical Information */}
      <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
        Medical Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Primary Health Concern"
            multiline
            rows={3}
            placeholder="Describe the main reason for registration and current health concerns..."
            value={data.primaryConcern || ""}
            onChange={(e) => handleChange("primaryConcern", e.target.value)}
            helperText="Please describe your primary health concerns or reason for seeking care"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="CKD Stage"
            value={data.ckdStage || ""}
            onChange={(e) => handleChange("ckdStage", e.target.value)}
            helperText="Select current CKD stage"
          >
            <MenuItem value="">
              <em>Not Diagnosed</em>
            </MenuItem>
            {ckdStages.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Diagnosis Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={data.diagnosisDate || ""}
            onChange={(e) => handleChange("diagnosisDate", e.target.value)}
            helperText="Date of CKD diagnosis"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            select
            label="Dialysis Modality"
            value={data.dialysisModality || ""}
            onChange={(e) => handleChange("dialysisModality", e.target.value)}
            helperText="Current kidney replacement therapy status"
          >
            <MenuItem value="">Select Modality</MenuItem>
            <MenuItem value="Pre-dialysis">Pre-dialysis</MenuItem>
            <MenuItem value="Hemodialysis">Hemodialysis</MenuItem>
            <MenuItem value="Peritoneal Dialysis">Peritoneal Dialysis</MenuItem>
            <MenuItem value="Transplant">Transplant</MenuItem>
          </TextField>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DemographicsTab;
